import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Search, Users, Phone, Mail, Calendar, GraduationCap, Building, MapPin, Loader2 } from 'lucide-react';

const GraduadosDirectorio = () => {
  const { user, API, getImageUrl } = useContext(AuthContext);
  const [graduados, setGraduados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({ total: 0, profesores: 0, estudiantes: 0 });

  useEffect(() => {
    loadGraduados();
    if (user?.rol === 'admin') {
      loadStats();
    }
  }, []);

  const loadGraduados = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/graduados`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Error al cargar graduados');
      const data = await response.json();
      setGraduados(data.graduados || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar el directorio de graduados');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/graduados/estadisticas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const filteredGraduados = graduados.filter(g =>
    `${g.nombre} ${g.apellido} ${g.email}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!user?.es_graduado && user?.rol !== 'admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <GraduationCap className="w-10 h-10 text-purple-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Acceso Restringido</h2>
            <p className="text-gray-600 mb-4">
              Esta sección es exclusiva para graduados del Instituto Bíblico Apostólico.
            </p>
            <p className="text-sm text-gray-500">
              Si eres graduado y no tienes acceso, contacta al administrador.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
            <GraduationCap className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Directorio de Graduados</h1>
            <p className="text-gray-600 text-sm">Comunidad de egresados del IBA</p>
          </div>
        </div>
      </div>

      {/* Stats Cards - Solo Admin */}
      {user?.rol === 'admin' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-xs">Total Graduados</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Users className="w-8 h-8 text-purple-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-indigo-100 text-xs">Activos</p>
                  <p className="text-2xl font-bold">{stats.activos}</p>
                </div>
                <Users className="w-8 h-8 text-indigo-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-xs">Profesores</p>
                  <p className="text-2xl font-bold">{stats.profesores}</p>
                </div>
                <GraduationCap className="w-8 h-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-cyan-100 text-xs">Estudiantes</p>
                  <p className="text-2xl font-bold">{stats.estudiantes}</p>
                </div>
                <Users className="w-8 h-8 text-cyan-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Buscar por nombre o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Graduados Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : filteredGraduados.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchTerm ? 'No se encontraron graduados con ese criterio' : 'No hay graduados registrados aún'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredGraduados.map((graduado) => (
            <Card key={graduado.id} className="hover:shadow-lg transition-shadow overflow-hidden">
              <CardContent className="p-0">
                {/* Header con gradiente */}
                <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-4">
                  <div className="flex items-center gap-3">
                    {graduado.foto_perfil ? (
                      <img
                        src={getImageUrl(graduado.foto_perfil)}
                        alt={graduado.nombre}
                        className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-md"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                        <span className="text-white text-xl font-bold">
                          {graduado.nombre?.[0]}{graduado.apellido?.[0]}
                        </span>
                      </div>
                    )}
                    <div>
                      <h3 className="text-white font-bold">{graduado.nombre} {graduado.apellido}</h3>
                      <span className="inline-block px-2 py-0.5 bg-white/20 rounded-full text-xs text-white capitalize">
                        {graduado.rol}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Info */}
                <div className="p-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail className="w-4 h-4 text-purple-500" />
                    <span className="truncate">{graduado.email}</span>
                  </div>
                  {graduado.celular && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone className="w-4 h-4 text-green-500" />
                      <span>{graduado.celular}</span>
                    </div>
                  )}
                  {graduado.año_graduacion && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4 text-blue-500" />
                      <span>Graduado en {graduado.año_graduacion}</span>
                    </div>
                  )}
                  {graduado.programa_cursado && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Building className="w-4 h-4 text-indigo-500" />
                      <span>{graduado.programa_cursado}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Footer count */}
      <div className="mt-6 text-center text-sm text-gray-500">
        Mostrando {filteredGraduados.length} de {graduados.length} graduados
      </div>
    </div>
  );
};

export default GraduadosDirectorio;
